import {
	AlertTriangle,
	ArrowRight,
	Check,
	CheckCircle2,
	Download,
	Loader2,
	Plus,
	Upload,
	X,
} from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
	useCreateSystemBranch,
	useUpdateBranchCustomName,
	useUpdateCROverrides,
} from '@/api/queries/organization-overrides-queries'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { OrgBranch } from '@/types/organization'
import {
	type CrMovedDiff,
	computeOrgDiff,
	downloadOrgXlsx,
	generateBranchCode,
	type OrgDiffResult,
	parseOrgXlsx,
} from '@/utils/org-xlsx'

interface CsvImportTabProps {
	branches: OrgBranch[]
}

export function CsvImportTab({ branches }: CsvImportTabProps) {
	const { t } = useTranslation()
	const fileInputRef = useRef<HTMLInputElement>(null)
	const createBranchMutation = useCreateSystemBranch()
	const renameBranchMutation = useUpdateBranchCustomName()
	const updateCRMutation = useUpdateCROverrides()

	const [file, setFile] = useState<File | null>(null)
	const [diff, setDiff] = useState<OrgDiffResult | null>(null)
	const [parseError, setParseError] = useState<string | null>(null)
	const [isApplying, setIsApplying] = useState(false)

	/* ── File handling ── */

	const processFile = async (f: File) => {
		setFile(f)
		setParseError(null)
		setDiff(null)
		const buffer = await f.arrayBuffer()
		const rows = parseOrgXlsx(buffer)
		if (rows.length === 0) {
			setParseError(t('administration.orgOverrides.csv.parseError'))
			return
		}
		setDiff(computeOrgDiff(branches, rows))
	}

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const f = e.target.files?.[0]
		if (f) processFile(f)
		// reset so the same file can be re-selected after clearing
		if (fileInputRef.current) fileInputRef.current.value = ''
	}

	const handleClearFile = () => {
		setFile(null)
		setDiff(null)
		setParseError(null)
	}

	/* ── Apply changes ── */

	const handleApply = async () => {
		if (!diff) return
		setIsApplying(true)
		try {
			// Step 1: create new virtual branches and collect their IDs
			const branchIdMap = new Map<string, string>()
			for (const nb of diff.newBranches) {
				const code = generateBranchCode(nb.branchName)
				const created = await createBranchMutation.mutateAsync({
					name: nb.branchName,
					code,
					customName: nb.branchName,
				})
				branchIdMap.set(nb.branchName.toLowerCase(), created.id)
			}

			// Step 2: rename existing branches
			for (const renamed of diff.renamedBranches) {
				await renameBranchMutation.mutateAsync({
					id: renamed.branchId,
					body: { customName: renamed.toName },
				})
			}

			// Step 3: rename CRs
			for (const renamed of diff.renamedCRs) {
				await updateCRMutation.mutateAsync({
					id: renamed.crId,
					body: { customName: renamed.toName },
				})
			}

			// Step 4: move CRs
			let count = 0
			for (const moved of diff.movedCRs) {
				if (moved.action.kind === 'MOVE_TO_VIRTUAL') {
					await updateCRMutation.mutateAsync({
						id: moved.crId,
						body: { systemBranchId: moved.action.targetBranchId },
					})
				} else if (moved.action.kind === 'MOVE_TO_NEW_VIRTUAL') {
					const branchId = branchIdMap.get(
						moved.action.branchName.toLowerCase(),
					)
					if (branchId) {
						await updateCRMutation.mutateAsync({
							id: moved.crId,
							body: { systemBranchId: branchId },
						})
					}
				} else {
					// UNDO_MOVE: return CR to original Protheus branch
					await updateCRMutation.mutateAsync({
						id: moved.crId,
						body: { systemBranchId: null },
					})
				}
				count++
			}

			count += diff.renamedBranches.length + diff.renamedCRs.length

			toast.success(
				t('administration.orgOverrides.csv.applySuccess', { count }),
			)
			handleClearFile()
		} catch {
			toast.error(t('administration.orgOverrides.csv.applyError'))
		}
		setIsApplying(false)
	}

	const totalChanges =
		(diff?.newBranches.length ?? 0) +
		(diff?.renamedBranches.length ?? 0) +
		(diff?.renamedCRs.length ?? 0) +
		(diff?.movedCRs.length ?? 0)

	return (
		<div className="space-y-4">
			{/* Step 1 — Download */}
			<StepCard
				step={1}
				title={t('administration.orgOverrides.csv.step1Title')}
			>
				<p className="text-sm text-muted-foreground">
					{t('administration.orgOverrides.csv.step1Desc')}
				</p>
				<Button
					variant="outline"
					size="sm"
					onClick={() => downloadOrgXlsx(branches)}
					className="gap-2 mt-2"
				>
					<Download className="size-4" />
					{t('administration.orgOverrides.csv.downloadButton')}
				</Button>
			</StepCard>

			{/* Step 2 — Upload */}
			<StepCard
				step={2}
				title={t('administration.orgOverrides.csv.step2Title')}
			>
				<p className="text-sm text-muted-foreground">
					{t('administration.orgOverrides.csv.step2Desc')}
				</p>
				<div className="flex items-center gap-3 mt-2">
					<input
						ref={fileInputRef}
						id="csv-upload"
						type="file"
						accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
						onChange={handleFileChange}
						className="sr-only"
					/>
					<label
						htmlFor="csv-upload"
						className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
					>
						<Upload className="size-4" />
						{file
							? file.name
							: t('administration.orgOverrides.csv.uploadButton')}
					</label>
					{file && (
						<button
							type="button"
							onClick={handleClearFile}
							className="text-muted-foreground hover:text-foreground transition-colors"
							aria-label={t('administration.orgOverrides.csv.clearButton')}
						>
							<X className="size-4" />
						</button>
					)}
				</div>
				{parseError && (
					<p className="mt-2 text-sm text-destructive">{parseError}</p>
				)}
			</StepCard>

			{/* Step 3 — Preview */}
			{diff && (
				<StepCard
					step={3}
					title={t('administration.orgOverrides.csv.step3Title')}
				>
					{/* Summary badges */}
					<div className="flex flex-wrap gap-2 mb-4">
						{diff.newBranches.length > 0 && (
							<DiffBadge color="green">
								<Plus className="size-3" />
								{t('administration.orgOverrides.csv.newBranchesCount', {
									count: diff.newBranches.length,
								})}
							</DiffBadge>
						)}
						{diff.renamedBranches.length > 0 && (
							<DiffBadge color="blue">
								<Check className="size-3" />
								{t('administration.orgOverrides.csv.renamedBranchesCount', {
									count: diff.renamedBranches.length,
								})}
							</DiffBadge>
						)}
						{diff.renamedCRs.length > 0 && (
							<DiffBadge color="blue">
								<Check className="size-3" />
								{t('administration.orgOverrides.csv.renamedCRsCount', {
									count: diff.renamedCRs.length,
								})}
							</DiffBadge>
						)}
						{diff.movedCRs.length > 0 && (
							<DiffBadge color="blue">
								<ArrowRight className="size-3" />
								{t('administration.orgOverrides.csv.movedCRsCount', {
									count: diff.movedCRs.length,
								})}
							</DiffBadge>
						)}
						{diff.notFound.length > 0 && (
							<DiffBadge color="amber">
								<AlertTriangle className="size-3" />
								{t('administration.orgOverrides.csv.notFoundCount', {
									count: diff.notFound.length,
								})}
							</DiffBadge>
						)}
						<DiffBadge color="muted">
							<CheckCircle2 className="size-3" />
							{t('administration.orgOverrides.csv.unchangedCount', {
								count: diff.unchangedCount,
							})}
						</DiffBadge>
					</div>

					{/* New branches section */}
					{diff.newBranches.length > 0 && (
						<section className="mb-4 space-y-2">
							<SectionLabel className="text-green-700">
								{t('administration.orgOverrides.csv.sectionNewBranches')}
							</SectionLabel>
							{diff.newBranches.map((nb) => (
								<div
									key={nb.branchName}
									className="rounded-md border border-green-200 bg-green-50/50 px-3 py-2"
								>
									<div className="flex items-center gap-2">
										<Plus className="size-3.5 text-green-600 shrink-0" />
										<span className="text-sm font-medium text-green-800">
											{nb.branchName}
										</span>
										<span className="text-xs text-green-600 font-mono opacity-70">
											({generateBranchCode(nb.branchName)})
										</span>
									</div>
									<p className="mt-0.5 ml-5 text-xs text-muted-foreground">
										{t('administration.orgOverrides.csv.crCount', {
											count: nb.crs.length,
										})}
									</p>
								</div>
							))}
						</section>
					)}

					{/* Renamed branches section */}
					{diff.renamedBranches.length > 0 && (
						<section className="mb-4 space-y-1">
							<SectionLabel>
								{t('administration.orgOverrides.csv.sectionRenamedBranches')}
							</SectionLabel>
							<div className="rounded-md border divide-y overflow-hidden">
								{diff.renamedBranches.map((renamed) => (
									<div
										key={renamed.branchId}
										className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted/20"
									>
										{renamed.branchCode && (
											<span className="font-mono text-xs text-muted-foreground shrink-0 bg-muted/40 px-1.5 py-0.5 rounded">
												{renamed.branchCode}
											</span>
										)}
										<span className="text-xs text-muted-foreground shrink-0 max-w-[120px] truncate">
											{renamed.fromName}
										</span>
										<ArrowRight className="size-3 text-muted-foreground shrink-0" />
										<span className="text-xs font-medium text-blue-700 shrink-0 max-w-[120px] truncate">
											{renamed.toName}
										</span>
									</div>
								))}
							</div>
						</section>
					)}

					{/* Moved CRs section */}
					{diff.renamedCRs.length > 0 && (
						<section className="mb-4 space-y-1">
							<SectionLabel>
								{t('administration.orgOverrides.csv.sectionRenamedCRs')}
							</SectionLabel>
							<div className="rounded-md border divide-y overflow-hidden">
								{diff.renamedCRs.map((renamed) => (
									<div
										key={renamed.crId}
										className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted/20"
									>
										<span className="font-mono text-xs text-muted-foreground shrink-0 bg-muted/40 px-1.5 py-0.5 rounded">
											{renamed.crCode}
										</span>
										<span className="text-xs text-muted-foreground shrink-0 max-w-[140px] truncate">
											{renamed.fromName}
										</span>
										<ArrowRight className="size-3 text-muted-foreground shrink-0" />
										<span className="text-xs font-medium text-blue-700 shrink-0 max-w-[140px] truncate">
											{renamed.toName}
										</span>
									</div>
								))}
							</div>
						</section>
					)}

					{/* Moved CRs section */}
					{diff.movedCRs.length > 0 && (
						<section className="mb-4 space-y-1">
							<SectionLabel>
								{t('administration.orgOverrides.csv.sectionMovedCRs')}
							</SectionLabel>
							<div className="rounded-md border divide-y overflow-hidden">
								{diff.movedCRs.map((moved) => (
									<MovedCRRow key={moved.crId} item={moved} />
								))}
							</div>
						</section>
					)}

					{/* Not found section */}
					{diff.notFound.length > 0 && (
						<section className="mb-4 space-y-1">
							<SectionLabel className="text-amber-600">
								{t('administration.orgOverrides.csv.sectionNotFound')}
							</SectionLabel>
							<div className="rounded-md border border-amber-200 divide-y overflow-hidden">
								{diff.notFound.map((nf) => (
									<div
										key={nf.crCode}
										className="flex items-center gap-2 px-3 py-2 bg-amber-50/50"
									>
										<AlertTriangle className="size-3.5 text-amber-500 shrink-0" />
										<span className="font-mono text-xs text-muted-foreground shrink-0 bg-muted/40 px-1.5 py-0.5 rounded">
											{nf.crCode}
										</span>
										<span className="text-sm truncate flex-1">{nf.crName}</span>
										<span className="text-xs text-muted-foreground shrink-0">
											→ {nf.filial}
										</span>
									</div>
								))}
							</div>
						</section>
					)}

					{/* No changes message */}
					{totalChanges === 0 && diff.notFound.length === 0 && (
						<p className="text-sm text-muted-foreground flex items-center gap-2">
							<CheckCircle2 className="size-4 text-green-500" />
							{t('administration.orgOverrides.csv.noChanges')}
						</p>
					)}

					{/* Apply / Clear actions */}
					{totalChanges > 0 && (
						<div className="flex gap-3 pt-3 border-t mt-2">
							<Button
								variant="outline"
								size="sm"
								onClick={handleClearFile}
								disabled={isApplying}
							>
								{t('administration.orgOverrides.csv.clearButton')}
							</Button>
							<Button
								size="sm"
								onClick={handleApply}
								disabled={isApplying}
								className="gap-2 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
							>
								{isApplying ? (
									<Loader2 className="size-4 animate-spin" />
								) : (
									<Check className="size-4" />
								)}
								{t('administration.orgOverrides.csv.applyButton', {
									count: totalChanges,
								})}
							</Button>
						</div>
					)}
				</StepCard>
			)}
		</div>
	)
}

// ── Internal sub-components ───────────────────────────────────────────────────

function StepCard({
	step,
	title,
	children,
}: {
	step: number
	title: string
	children: React.ReactNode
}) {
	return (
		<div className="rounded-lg border bg-card p-4 space-y-1">
			<div className="flex items-center gap-2 mb-2">
				<span className="size-6 rounded-full bg-kaefer-dark-grey text-white text-xs font-bold flex items-center justify-center shrink-0">
					{step}
				</span>
				<h3 className="text-sm font-medium">{title}</h3>
			</div>
			{children}
		</div>
	)
}

function SectionLabel({
	children,
	className,
}: {
	children: React.ReactNode
	className?: string
}) {
	return (
		<h4
			className={cn(
				'text-xs font-semibold uppercase tracking-wide text-muted-foreground',
				className,
			)}
		>
			{children}
		</h4>
	)
}

type BadgeColor = 'green' | 'blue' | 'amber' | 'muted'
const badgeColorMap: Record<BadgeColor, string> = {
	green: 'bg-green-100 text-green-800 border-green-200',
	blue: 'bg-blue-100 text-blue-800 border-blue-200',
	amber: 'bg-amber-100 text-amber-800 border-amber-200',
	muted: 'bg-muted text-muted-foreground border-border',
}

function DiffBadge({
	color,
	children,
}: {
	color: BadgeColor
	children: React.ReactNode
}) {
	return (
		<span
			className={cn(
				'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium',
				badgeColorMap[color],
			)}
		>
			{children}
		</span>
	)
}

function MovedCRRow({ item }: { item: CrMovedDiff }) {
	const isUndo = item.action.kind === 'UNDO_MOVE'
	const isNew = item.action.kind === 'MOVE_TO_NEW_VIRTUAL'
	return (
		<div className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted/20">
			<span className="font-mono text-xs text-muted-foreground shrink-0 bg-muted/40 px-1.5 py-0.5 rounded">
				{item.crCode}
			</span>
			<span className="flex-1 truncate min-w-0">{item.crName}</span>
			<span className="text-xs text-muted-foreground shrink-0 max-w-[90px] truncate">
				{item.fromBranch}
			</span>
			<ArrowRight className="size-3 text-muted-foreground shrink-0" />
			<span
				className={cn(
					'text-xs font-medium shrink-0 max-w-[90px] truncate',
					isNew
						? 'text-green-700'
						: isUndo
							? 'text-amber-700'
							: 'text-blue-700',
				)}
			>
				{item.toBranch}
			</span>
		</div>
	)
}
